import { Inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable, merge } from 'rxjs';
import { map } from 'rxjs/operators';

import { SHARED_ENVIRONMENT, SharedEnvironment } from '@sd-portal/shared/environment';
import { PermissionsService, permissionTypes } from '@sd-portal/shared/permissions';
import { NavMenuItem } from './_models/nav-menu-item.model';
import { getBATRoute } from './_functions/feature-routing.function';
import { BuildTripViewType, CalendarViewType } from '@sd-portal/shared/models';
import { UserConfigurationFacade } from 'libs/shared/user-configuration/src/lib/+state/user-configuration.facade';

@Injectable({ providedIn: 'root' })
export class SdNavService {
  private readonly showSideMenu: BehaviorSubject<boolean> = new BehaviorSubject(false);

  showSideMenu$ = this.showSideMenu.asObservable();

  calendarViewType: CalendarViewType;
  buildTripViewTypeId: BuildTripViewType | null;
  batRoute: string = 'pre-flight/schedule';
  constructor(
    private readonly permissionsService: PermissionsService,
    private readonly userConfigurationFacade: UserConfigurationFacade,
    @Inject(SHARED_ENVIRONMENT) private readonly environment: SharedEnvironment
  ) {}

  setShowSideMenu(show: boolean = true) {
    this.showSideMenu.next(show);
  }

  getRoutes(): Observable<NavMenuItem[]> {
    const permissions: string[] = [];

    return merge(
      // profile
      this.permissionsService.currentUserCan(permissionTypes.profile.aircraft.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('profile.aircraft.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService.currentUserCan(permissionTypes.profile.airport.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('profile.airport.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService.currentUserCan(permissionTypes.profile.checklist.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('profile.checklist.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService.currentUserCan(permissionTypes.profile.company.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('profile.company.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService.currentUserCan(permissionTypes.profile.people.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('profile.people.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService
        .currentUserCan(permissionTypes.profile.trainingRequirement.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('profile.trainingRequirement.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService
        .currentUserCan(permissionTypes.profile.rolesManagement.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('profile.rolesManagement.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService
        .currentUserCan(permissionTypes.profile.userManagement.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('profile.userManagement.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService.currentUserCan(permissionTypes.profile.eSignature.writeAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('profile.eSignature.writeAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService
        .currentUserCan(permissionTypes.profile.organizationalView.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('profile.organizationalView.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService
        .currentUserCan(permissionTypes.profile.integrationManagement.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('profile.integrationManagement.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      // preflight
      this.permissionsService.currentUserCan(permissionTypes.preflight.linkAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('preflight.linkAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService.currentUserCan(permissionTypes.preflight.buildTrip.writeAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('preflight.buildTrip.writeAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService
        .currentUserCan(permissionTypes.preflight.fuelVendorUpload.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('preflight.fuelVendorUpload.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService
        .currentUserCan(permissionTypes.preflight.acknowledgements.writeAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('preflight.acknowledgements.writeAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      // postflight
      this.permissionsService
        .currentUserCan(permissionTypes.postFlight.tripManager.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('postFlight.tripManager.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService
        .currentUserCan(permissionTypes.postFlight.pilotLogbook.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('postFlight.pilotLogbook.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),
      this.permissionsService.currentUserCan(permissionTypes.postFlight.expenses.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('postFlight.expense.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      this.permissionsService.currentUserCan(permissionTypes.postFlight.sifl.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('postFlight.sifl.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      // - reporting
      this.permissionsService.currentUserCan(permissionTypes.postFlight.reports.readAccess).pipe(
        map(val => {
          if (val) {
            permissions.push('postFlight.reports.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),
      // - telerik reporting
      this.permissionsService.currentUserCan(permissionTypes.beta.canViewReports).pipe(
        map(val => {
          if (val) {
            permissions.push('beta.reports.readAccess');
          }
          return this.initRoutes(permissions);
        })
      ),

      // admin
      this.permissionsService
        .currentUserCan(permissionTypes.admin.HistoricalUpload.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('admin.historicalUpload.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),

      //monthlyCalendar
      this.permissionsService
        .currentUserCan(permissionTypes.preflight.monthlyCalendar.readAccess)
        .pipe(
          map(val => {
            if (val) {
              permissions.push('preflight.monthlyCalendar.readAccess');
            }
            return this.initRoutes(permissions);
          })
        ),

      //calendar view type
      this.userConfigurationFacade.calendarConfiguration$.pipe(
        map(val => {
          if (val) {
            this.calendarViewType = val.calendarViewTypeId;
            this.buildTripViewTypeId = val.buildTripViewTypeId;
            this.batRoute = getBATRoute(this.buildTripViewTypeId).substring(1);
          }
          return this.initRoutes(permissions);
        })
      )
    );
  }

  private initRoutes(permissions: string[]): NavMenuItem[] {
    const menuItems: NavMenuItem[] = [];
    let subMenuItems: NavMenuItem[] = [];

    // profiles
    if (permissions.some(p => p === 'profile.aircraft.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Aircraft',
          `${this.environment.domainModuleRoutes.profiles}profile/aircraft`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.airport.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Airport',
          `${this.environment.domainModuleRoutes.profiles}profile/airport`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.checklist.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Checklist',
          `${this.environment.domainModuleRoutes.profiles}profile/checklist`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.company.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Company',
          `${this.environment.domainModuleRoutes.profiles}profile/company`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.people.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'People',
          `${this.environment.domainModuleRoutes.profiles}profile/persons`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.trainingRequirement.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Training Requirement',
          `${this.environment.domainModuleRoutes.profiles}profile/training-requirement`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.rolesManagement.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Roles Management',
          `${this.environment.domainModuleRoutes.profiles}profile/manage-roles`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.userManagement.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'User Management',
          `${this.environment.domainModuleRoutes.profiles}profile/user-management`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.eSignature.writeAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'User E-Signature',
          `${this.environment.domainModuleRoutes.profiles}profile/user-signature`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.organizationalView.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Organizational View',
          `${this.environment.domainModuleRoutes.profiles}profile/organizational-view`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'profile.integrationManagement.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Integration Management',
          `${this.environment.domainModuleRoutes.profiles}profile/integration-management`,
          false
        )
      );
    }

    if (subMenuItems.length > 0) {
      menuItems.push(
        new NavMenuItem(
          'Profiles',
          `${this.environment.domainModuleRoutes.profiles}profile/`,
          false,
          subMenuItems
        )
      );
    }

    // preflight
    subMenuItems = [];
    if (permissions.some(p => p === 'preflight.linkAccess')) {
      const calendarPath =
        this.calendarViewType === CalendarViewType.MonthBeta
          ? 'pre-flight/calendar/month'
          : 'pre-flight/calendar/default';
      subMenuItems.push(
        new NavMenuItem(
          'Calendar',
          `${this.environment.domainModuleRoutes.preFlight + calendarPath}`,
          false
        )
      );
    }
    // Calendar BETA nav entry is permanently disabled; calendar-beta now redirects to calendar.

    if (permissions.some(p => p === 'preflight.buildTrip.writeAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Schedule A Trip',
          `${this.environment.domainModuleRoutes.preFlight + this.batRoute}`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'preflight.fuelVendorUpload.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Fuel Vendor Price Upload',
          `${this.environment.domainModuleRoutes.preFlight}pre-flight/fuel-vendor-price-upload`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'preflight.buildTrip.writeAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Draft Trip',
          `${this.environment.domainModuleRoutes.preFlight}pre-flight/draft-trip`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'preflight.acknowledgements.writeAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Crew Acknowledgements',
          `${this.environment.domainModuleRoutes.preFlight}pre-flight/crew-acknowledgements`,
          false
        )
      );
    }

    if (subMenuItems.length > 0) {
      menuItems.push(
        new NavMenuItem(
          'Preflight',
          `${this.environment.domainModuleRoutes.preFlight}pre-flight/`,
          false,
          subMenuItems
        )
      );
    }

    // postflight
    subMenuItems = [];

    if (permissions.some(p => p === 'postFlight.tripManager.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Fleet Overview',
          `${this.environment.domainModuleRoutes.postFlight}post-flight/fleet-overview`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'postFlight.tripManager.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Trip Manager',
          `${this.environment.domainModuleRoutes.postFlight}post-flight/tripmanager`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'postFlight.pilotLogbook.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Pilot Logbook',
          `${this.environment.domainModuleRoutes.postFlight}post-flight/pilot-logbook`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'postFlight.expense.readAccess')) {
      subMenuItems.push(new NavMenuItem('Expense Capture', '/post-flight/expenses', false));
    }

    if (permissions.some(p => p === 'postFlight.tripManager.readAccess')) {
      subMenuItems.push(
        new NavMenuItem(
          'Squawks/Discrepancies',
          `${this.environment.domainModuleRoutes.postFlight}post-flight/squawks`,
          false
        )
      );
    }

    if (permissions.some(p => p === 'postFlight.sifl.readAccess')) {
      subMenuItems.push(new NavMenuItem('SIFL Grouping', '/post-flight/sifl/grouping', false));
    }

    if (subMenuItems.length > 0) {
      menuItems.push(
        new NavMenuItem(
          'Postflight',
          `${this.environment.domainModuleRoutes.postFlight}post-flight/`,
          false,
          subMenuItems
        )
      );
    }

    // reporting beta telerik reports
    if (permissions.some(p => p === 'beta.reports.readAccess')) {
      menuItems.push(
        new NavMenuItem(
          'Reports',
          `${this.environment.domainModuleRoutes.postFlight}reports`,
          false
        )
      );
    }

    // support
    subMenuItems = [];

    if (subMenuItems.length > 0) {
      menuItems.push(
        new NavMenuItem(
          'Support',
          `${this.environment.domainModuleRoutes.support}/`,
          false,
          subMenuItems
        )
      );
    }

    // admin
    subMenuItems = [];
    if (permissions.some(p => p.includes('admin'))) {
      if (permissions.some(p => p === 'admin.historicalUpload.readAccess')) {
        subMenuItems.push(
          new NavMenuItem(
            'Historical Upload',
            `${this.environment.domainModuleRoutes.admin}admin/HistoricalUpload`,
            false
          )
        );
        subMenuItems.push(
          new NavMenuItem(
            'Flight Plan Simulator',
            `${this.environment.domainModuleRoutes.admin}admin/FlightPlanSimulator`,
            false
          )
        );
      }

      if (subMenuItems.length > 0) {
        menuItems.push(
          new NavMenuItem(
            'Admin',
            `${this.environment.domainModuleRoutes.admin}admin/`,
            false,
            subMenuItems
          )
        );
      }
    }
    // training videos
    menuItems.push(
      new NavMenuItem(
        'Training Videos',
        `${this.environment.domainModuleRoutes.trainingVideos}training-videos`,
        false
      )
    );

    return menuItems;
  }
}
